"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseComboboxCursorProps {
  /** Whether the option list is on screen — inline (`withoutPopover`) lists always are. */
  contentVisible: boolean;
  /** The consumer's `onValueChange`, forwarded after the cursor state is updated. */
  onValueChange?: (value: string) => void;
}

/**
 * Scrolls `list` so `active` is visible, by moving `list` alone.
 *
 * `Element.scrollIntoView` would walk *every* scrollable ancestor — including ones with `overflow: hidden`,
 * which are still programmatically scrollable. In the mobile drawer that scrolled the drawer body too and
 * pushed the search field off the top of the sheet.
 */
const scrollRowIntoView = (list: HTMLDivElement, active: Element, block: "start" | "nearest") => {
  const listBox = list.getBoundingClientRect();
  const rowBox = active.getBoundingClientRect();
  // The popover scales up as it opens, so client rects are still shrunk when this runs while `scrollTop`
  // is in untransformed pixels. The list's own two heights give the factor to convert between them.
  const scale = list.offsetHeight > 0 ? listBox.height / list.offsetHeight : 1;
  const toScrollPx = (distance: number) => (scale > 0 ? distance / scale : distance);
  if (block === "start") {
    list.scrollTop += toScrollPx(rowBox.top - listBox.top);
    return;
  }
  const overflowTop = listBox.top - rowBox.top;
  const overflowBottom = rowBox.bottom - listBox.bottom;
  if (overflowTop > 0) {
    list.scrollTop -= toScrollPx(overflowTop);
  } else if (overflowBottom > 0) {
    list.scrollTop += toScrollPx(overflowBottom);
  }
};

/**
 * Owns cmdk's *active* row — the one arrow keys move — so that the dropdown can open with the cursor on the
 * item marked `selected` rather than on cmdk's default first item, and keeps that row scrolled into view.
 *
 * Controlling cmdk's `value` is what makes the open positioning possible, and it is also what forces the rest
 * of this hook: it disarms cmdk's built-in follow-scroll (that scroll flushes before the controlled value
 * round-trips through React, so it measures the *previous* active row) and it opens a few seams where cmdk's
 * own cursor bookkeeping no longer fires. Each is handled below.
 */
const useComboboxCursor = ({ contentVisible, onValueChange }: UseComboboxCursorProps) => {
  const [activeValue, setActiveValue] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  // Armed on open (and on mount, for inline `withoutPopover` lists): the first selected Item to mount
  // positions the cursor. Disarmed once positioned, on typing, and on close — late-arriving options
  // (search results, load-more pages) must not yank the list while the user is browsing.
  const pendingOpenScrollRef = useRef(true);
  // The cmdk token that still owes a "start" scroll (the open positioning). Matched against the *rendered*
  // active row rather than consumed by whichever effect run happens to fire first — the run scheduled for
  // the pre-report render lands in the same frame as the report itself and would otherwise eat the flag
  // while the DOM still shows cmdk's first row as active.
  const startScrollTokenRef = useRef<string | null>(null);
  // Bumped on every keystroke so the cursor effect re-runs even when clearing `activeValue` is a no-op
  // (it is already ""), and set alongside it: cmdk skips its own select-first pass when the new best
  // match is the row its *internal* value still holds, and our reset then leaves the list with no
  // highlighted row at all. The effect repairs that by adopting the first row.
  const [searchTick, setSearchTick] = useState(0);
  const repairCursorRef = useRef(false);
  // True while the active row is moving because the pointer is over the list: cmdk selects the hovered
  // row, and following it with a scroll would slide a partly-visible row under the cursor — which hovers
  // the next row, and so on. Keyboard input takes the cursor back.
  const pointerActiveRef = useRef(false);

  /** Re-arms the open positioning; call before propagating an open/close to the rest of the combobox. */
  const prepareOpenChange = useCallback((open: boolean) => {
    pendingOpenScrollRef.current = open;
    startScrollTokenRef.current = null;
    if (open) {
      // Clear the cursor so cmdk's select-first-item default applies when no item reports as selected.
      setActiveValue("");
    }
  }, []);

  const handleValueChange = useCallback(
    (next: string) => {
      setActiveValue(next);
      onValueChange?.(next);
    },
    [onValueChange]
  );

  /** A mounted Item whose `selected` prop is true announces its cmdk token. */
  const reportSelectedItem = useCallback((token: string) => {
    if (!pendingOpenScrollRef.current) {
      return;
    }
    // Deferred a frame so this wins over cmdk's own scheduled select-first-item pass, which flushes
    // after the reporting Item's effect and would otherwise clobber the cursor with the first row.
    requestAnimationFrame(() => {
      if (!pendingOpenScrollRef.current) {
        return;
      }
      pendingOpenScrollRef.current = false;
      startScrollTokenRef.current = token;
      setActiveValue(token);
    });
  }, []);

  /** The Input announces typing, so the list snaps back to the first match for the new term. */
  const notifySearchChanged = useCallback(() => {
    pendingOpenScrollRef.current = false;
    startScrollTokenRef.current = null;
    repairCursorRef.current = true;
    setSearchTick(tick => tick + 1);
    // Clearing the cursor makes cmdk re-select the first match: immediately for client-filtered lists,
    // and on item mount for server-searched lists (shouldFilter={false}), where cmdk otherwise keeps the
    // stale cursor — and with it the old scroll position. Scrolling to top covers the loading gap.
    setActiveValue("");
    listRef.current?.scrollTo({ top: 0 });
  }, []);

  // Which input is driving the cursor, so hovering can move it without dragging the scroll along.
  useEffect(() => {
    if (!contentVisible) {
      return;
    }
    const markPointer = () => {
      pointerActiveRef.current = true;
    };
    const markKeyboard = () => {
      pointerActiveRef.current = false;
    };
    document.addEventListener("pointermove", markPointer, true);
    document.addEventListener("keydown", markKeyboard, true);
    return () => {
      document.removeEventListener("pointermove", markPointer, true);
      document.removeEventListener("keydown", markKeyboard, true);
    };
  }, [contentVisible]);

  // Keep the active row visible whenever it moves (open positioning, arrow keys, cmdk re-selecting the
  // best match as the user types) — re-done here against the settled DOM, one frame after the controlled
  // value round-trips. "nearest" keeps it a no-op while the row is already visible.
  useEffect(() => {
    if (!contentVisible) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      const active = list?.querySelector('[cmdk-item][aria-selected="true"]');
      if (!list || !active) {
        // No highlighted row after a search: take the first one so the cursor — and Enter — keep working.
        if (!repairCursorRef.current) {
          return;
        }
        const token = list?.querySelector('[cmdk-item]:not([aria-disabled="true"])')?.getAttribute("data-value");
        if (token != null) {
          repairCursorRef.current = false;
          setActiveValue(token);
        }
        return;
      }
      repairCursorRef.current = false;
      const startToken = startScrollTokenRef.current;
      const isOpenPositioning = startToken != null && active.getAttribute("data-value") === startToken;
      const fromPointer = pointerActiveRef.current;
      pointerActiveRef.current = false;
      if (isOpenPositioning) {
        startScrollTokenRef.current = null;
      } else if (fromPointer) {
        // Hovering moved the cursor — leave the scroll where the user put it.
        return;
      }
      scrollRowIntoView(list, active, isOpenPositioning ? "start" : "nearest");
    });
    return () => cancelAnimationFrame(frame);
  }, [contentVisible, activeValue, searchTick]);

  return { activeValue, listRef, prepareOpenChange, handleValueChange, reportSelectedItem, notifySearchChanged };
};

export default useComboboxCursor;
